/**
 * Aide affichée à la demande (icône ⓘ) devant chaque droit — écrans Rôles et droits d'un
 * utilisateur. Chaque texte décrit ce que le droit contrôle RÉELLEMENT côté API (routes
 * `requirePermission` / `requireDocTypePermission`, contrôles dans les services, rapports,
 * connecteur IA) : à tenir à jour quand un droit change de portée.
 *
 * Droits par type de document (`documentVentes.{TYPE}.{action}`) : texte construit selon
 * l'action. Droit absent de ce fichier → description de sys_permissions.
 */
import { Permission } from '../models/models';
import { labels } from './labels';

/** Pied de bulle : les droits de l'écran sont chargés à la connexion (AuthSlice). */
export const NOTE_RECONNEXION = "Un changement de droit s'applique à l'écran après reconnexion de l'utilisateur ; l'API l'applique aussitôt.";

export interface AidePermission {
  permet: string;      // ce que le droit autorise
  sans?: string;       // ce que voit / subit l'utilisateur sans ce droit
  avec?: string;       // droits à combiner
}

const docs   = labels.documentVentePluriel.toLowerCase();
const tiers  = labels.tiersPluriel.toLowerCase();
const biens  = labels.bienPluriel.toLowerCase();
const regl   = labels.reglementPluriel.toLowerCase();
const DOCS_G = `« ${labels.documentVentePluriel} › Global »`;

const AIDE: Record<string, AidePermission> = {
  // ── Documents : droits globaux (tous types) ────────────────────────────────
  'docVentes.read': {
    permet: `Voir les listes de ${docs} (web et mobile), générer le rapport « liste de documents », laisser l'assistant IA lire les ${docs}.`,
    sans: `Listes de ${docs} refusées (web et mobile).`,
    avec: `Ouvrir un document précis demande aussi le droit « Consulter » de son type.`,
  },
  'docVentes.create': {
    permet: `Transformer un document en document suivant (seul ou en masse, web et mobile) et dupliquer un document.`,
    sans: `Boutons de transformation et de duplication refusés.`,
    avec: `Le droit « Créer » du type produit est vérifié en plus ; pour dupliquer, aussi « Consulter » du document source.`,
  },
  'docVentes.update': {
    permet: `Cas de secours : enregistrer un document existant dont le type n'est pas transmis. Ne devrait jamais servir.`,
    avec: `La modification d'un document est contrôlée par le droit « Modifier » de son type.`,
  },
  'docVentes.delete': {
    permet: `Sans effet aujourd'hui.`,
    avec: `La suppression d'un document est contrôlée par le droit « Supprimer » de son type.`,
  },
  'docVentes.remise': {
    permet: `Accorder une remise : % de remise sur une ligne article, ligne de type REMISE. Tous types de document, toutes saisies (écran, import, point de vente, IA).`,
    sans: `Champ remise et bouton « Remise » masqués ; l'API refuse toute nouvelle remise (une remise déjà en place et inchangée passe).`,
  },
  'docVentes.prix': {
    permet: `Modifier le prix unitaire d'une ligne article d'un document ${labels.client.toLowerCase()} ou ${labels.fournisseur.toLowerCase()} (hors fabrication), à l'écran, à l'import, au point de vente.`,
    sans: `Champ prix grisé (« Prix catalogue ») : prix du département du document, sinon de la fiche ; l'API refuse tout autre prix (un prix inchangé passe).`,
  },
  'docVentes.venteSousCout': {
    permet: `Vendre sous le prix de revient (vente ${labels.client.toLowerCase()} hors avoir) : la ligne est acceptée avec un avertissement.`,
    sans: `L'API refuse une ligne dont le prix net HT (après remise) est inférieur au prix de revient. Contrôle ligne par ligne.`,
  },

  // ── Articles ───────────────────────────────────────────────────────────────
  'articles.read': {
    permet: `Voir la liste et les fiches articles, chercher un article dans une saisie (lignes, point de vente), aperçus des actions de masse, rapport « liste d'articles », lecture par l'assistant IA.`,
    sans: `Liste d'articles refusée ; impossible de choisir un article dans une ligne de document.`,
  },
  'articles.create': {
    permet: `Créer une fiche article et importer des articles depuis Excel.`,
    avec: `Le modèle d'import Excel est téléchargeable sans droit.`,
  },
  'articles.update': {
    permet: `Modifier une fiche article et appliquer les actions de masse (prix, famille, fournisseur, partage par département, valeurs par défaut).`,
    avec: `Prix propres à un département : droits « Prix par département ». Prix de revient : « Voir le prix de revient ».`,
  },
  'articles.delete': {
    permet: `Supprimer une fiche article.`,
  },
  'articles.prixRevient': {
    permet: `Voir le prix de revient : prix R des fiches et des départements, coûts et marges, valeur du stock au coût, compte de résultat, documents valorisés au coût (achats, inventaire, bon d'entrée).`,
    sans: `Ces valeurs sont masquées partout (écrans, rapports, assistant IA). Le prix de revient saisi est ignoré ; sur les achats / inventaire / bon d'entrée, le prix est posé par l'API.`,
  },

  // ── Prix par département ───────────────────────────────────────────────────
  'prixDepartements.read': {
    permet: `Voir les prix propres à chaque département (onglet de la fiche article, actions de masse).`,
    sans: `Onglet des prix par département masqué. Le prix du département en cours reste utilisé à la vente.`,
  },
  'prixDepartements.update': {
    permet: `Créer ou modifier un prix propre à un département (fiche article, copie depuis la famille, actions de masse).`,
    avec: `Le département visé doit être autorisé pour l'utilisateur (restrictions de scope).`,
  },
  'prixDepartements.delete': {
    permet: `Supprimer le prix propre à un département : le département reprend le prix de la fiche.`,
  },

  // ── Tiers ──────────────────────────────────────────────────────────────────
  'tiers.read': {
    permet: `Voir les ${tiers} (${labels.clientPluriel.toLowerCase()}, ${labels.fournisseurPluriel.toLowerCase()}, ${labels.employePluriel.toLowerCase()}) et leurs plafonds, les choisir dans un document, rapport « liste des ${tiers} », lecture par l'assistant IA.`,
    sans: `Listes refusées ; impossible de choisir un ${labels.tiers.toLowerCase()} dans une saisie.`,
  },
  'tiers.create': {
    permet: `Créer un ${labels.tiers.toLowerCase()}, importer depuis Excel, définir un plafond.`,
    avec: `Le modèle d'import Excel est téléchargeable sans droit.`,
  },
  'tiers.update': { permet: `Modifier un ${labels.tiers.toLowerCase()} et son plafond.` },
  'tiers.delete': { permet: `Supprimer un ${labels.tiers.toLowerCase()} ou son plafond.` },

  // ── Biens / contrats ───────────────────────────────────────────────────────
  'biens.read':   { permet: `Voir les ${biens}, leurs groupes et leurs statistiques revenus / dépenses.` },
  'biens.create': { permet: `Créer un ${labels.bien.toLowerCase()} ou un groupe.` },
  'biens.update': { permet: `Modifier un ${labels.bien.toLowerCase()} ou un groupe.` },
  'biens.delete': { permet: `Supprimer un ${labels.bien.toLowerCase()} ou un groupe.` },
  'contrats.read':   { permet: `Voir et rechercher les ${labels.contratPluriel}.` },
  'contrats.create': { permet: `Créer un ${labels.contrat}.` },
  'contrats.update': { permet: `Modifier un ${labels.contrat}.` },
  'contrats.delete': { permet: `Supprimer un ${labels.contrat}.` },

  // ── Règlements / comptes ───────────────────────────────────────────────────
  'reglements.read': {
    permet: `Voir les encaissements, paiements, mouvements de caisse, opérations diverses et l'état de caisse ; rapport « liste des ${regl} » ; lecture par l'assistant IA.`,
  },
  'reglements.create': {
    permet: `Saisir un encaissement, un paiement, une opération diverse ou un transfert entre caisses. Nécessaire aussi pour vendre au point de vente (l'encaissement y est obligatoire).`,
    avec: `Le département et la caisse utilisés doivent être autorisés pour l'utilisateur (restrictions de scope). Point de vente : aussi « Créer » du type FACT_CLIENT.`,
  },
  'reglements.update': { permet: `Modifier un règlement existant.` },
  'reglements.delete': { permet: `Supprimer un règlement.` },
  'comptes.read': {
    permet: `Voir les comptes et balances (${labels.clientPluriel.toLowerCase()}, ${labels.fournisseurPluriel.toLowerCase()}, ${labels.employePluriel.toLowerCase()}, caisses), relevés et balances en rapport, lecture par l'assistant IA.`,
  },
  'comptes.create': { permet: `Saisir une écriture de compte manuelle.` },
  'comptes.update': { permet: `Modifier une écriture de compte.` },

  // ── Stock ──────────────────────────────────────────────────────────────────
  'stock.read': {
    permet: `Voir l'état du stock et les mouvements ; rapports stock et mouvements ; lecture par l'assistant IA.`,
  },
  'stock.create': {
    permet: `Sans effet aujourd'hui.`,
    avec: `Le stock bouge par les documents (bons d'entrée / sortie, transferts, inventaire…), contrôlés par les droits de leur type.`,
  },
  'stock.update': {
    permet: `Sans effet aujourd'hui.`,
    avec: `Le stock bouge par les documents, contrôlés par les droits de leur type.`,
  },
  'stock.delete': {
    permet: `Sans effet aujourd'hui.`,
    avec: `Le stock bouge par les documents, contrôlés par les droits de leur type.`,
  },

  // ── Familles ───────────────────────────────────────────────────────────────
  'familles.read':   { permet: `Voir les familles et catégories (arborescence) et leurs valeurs par défaut.` },
  'familles.create': {
    permet: `Créer une famille ou catégorie, importer depuis Excel.`,
    avec: `Le modèle d'import Excel est téléchargeable sans droit.`,
  },
  'familles.update': { permet: `Modifier une famille, ses valeurs par défaut et son partage par département.` },
  'familles.delete': { permet: `Supprimer une famille ou catégorie.` },

  // ── Organisation ───────────────────────────────────────────────────────────
  'departements.read': {
    permet: `Voir l'écran de gestion des départements.`,
    avec: `Le choix du département de travail n'en dépend pas : il suit les restrictions de scope.`,
  },
  'departements.create': { permet: `Créer un département.` },
  'departements.update': { permet: `Modifier un département.` },
  'departements.delete': { permet: `Supprimer un département.` },
  'taches.read':   { permet: `Voir les tâches.` },
  'taches.create': { permet: `Créer une tâche.` },
  'taches.update': { permet: `Modifier une tâche.` },
  'taches.delete': { permet: `Supprimer une tâche.` },
  'immobilisations.read':   { permet: `Voir les immobilisations.` },
  'immobilisations.create': { permet: `Créer une immobilisation.` },
  'immobilisations.update': { permet: `Modifier une immobilisation.` },
  'immobilisations.delete': { permet: `Supprimer une immobilisation.` },

  // ── Utilisateurs & droits ──────────────────────────────────────────────────
  'users.read':   { permet: `Voir la liste des utilisateurs.` },
  'users.create': {
    permet: `Créer un compte utilisateur.`,
    avec: `Lui choisir un rôle demande en plus « Rôles & droits › Gérer les droits ».`,
  },
  'users.update': { permet: `Modifier un compte utilisateur.` },
  'users.delete': { permet: `Supprimer un compte utilisateur.` },
  'roles.read': {
    permet: `Voir les rôles et leurs droits, les droits, exceptions et restrictions d'un utilisateur.`,
  },
  'roles.create': { permet: `Créer un rôle.` },
  'roles.update': {
    permet: `Gérer les droits : droits d'un rôle, rôles d'un utilisateur, exceptions individuelles, restrictions de scope.`,
    sans: `L'API refuse toute modification de droits.`,
    avec: `À réserver aux administrateurs : ce droit permet de se donner n'importe quel accès.`,
  },
  'roles.delete': { permet: `Supprimer un rôle.` },

  // ── Divers ─────────────────────────────────────────────────────────────────
  'notifications.send': {
    permet: `Diffuser une notification dans l'application aux utilisateurs (écran Utilisateurs).`,
    avec: `Recevoir et lire ses propres notifications ne demande aucun droit.`,
  },
  'options.read': {
    permet: `Voir les options personnalisables et télécharger un modèle Word d'impression.`,
  },
  'options.update': {
    permet: `Modifier ou réinitialiser les options (y compris pour tous les départements) et déposer un modèle Word d'impression.`,
  },
  'aiToken.read':   { permet: `Voir les tokens du connecteur IA (MCP).` },
  'aiToken.create': {
    permet: `Générer un token pour brancher un agent IA externe (connecteur MCP).`,
    avec: `L'agent agit avec les droits de l'utilisateur qui a créé le token.`,
  },
  'aiToken.delete': { permet: `Révoquer un token du connecteur IA.` },
  'assistant.use': {
    permet: `Utiliser l'assistant IA intégré.`,
    avec: `L'assistant agit avec les droits de l'utilisateur : il ne voit que ce que l'utilisateur peut voir.`,
  },
};

/** Droits par type de document : `documentVentes.{TYPE}.{action}`. */
const aideTypeDocument = (type: string, action: string): AidePermission | null => {
  const t = `de type ${type}`;
  switch (action) {
    case 'read':
      return {
        permet: `Ouvrir un document ${t}, voir ses lignes et l'imprimer.`,
        sans: `Ces documents ne s'ouvrent pas et ne s'impriment pas.`,
        avec: `Les listes de documents dépendent de ${DOCS_G} › Consulter.`,
      };
    case 'create':
      return {
        permet: `Créer un document ${t}, saisir et importer ses lignes.${type === 'FACT_CLIENT' ? ` Nécessaire aussi pour le point de vente, avec « ${labels.reglementPluriel} › Créer ».` : ''}`,
        avec: `Aussi exigé quand on transforme ou duplique vers ce type (avec ${DOCS_G} › Créer).`,
      };
    case 'update':
      return {
        permet: `Modifier un document ${t} et saisir, modifier ou supprimer ses lignes.`,
        avec: `Pour les lignes, « Créer » ou « Modifier » suffit.`,
      };
    case 'delete':
      return { permet: `Supprimer un document ${t}.` };
    case 'cloturer':
      return { permet: `Clôturer un document ${t} : il est verrouillé, plus aucune modification possible.` };
    case 'decloturer':
      return { permet: `Rouvrir un document ${t} clôturé pour pouvoir le modifier.` };
    default:
      return null;
  }
};

/** Aide d'un droit ; à défaut, la description de sys_permissions. */
export const aidePermission = (p: Pick<Permission, 'code' | 'description'>): AidePermission | null => {
  if (AIDE[p.code]) return AIDE[p.code];
  const parts = p.code.split('.');
  if (parts[0] === 'documentVentes' && parts.length === 3) {
    const aide = aideTypeDocument(parts[1], parts[2]);
    if (aide) return aide;
  }
  return p.description ? { permet: p.description } : null;
};
